import React from 'react';

const OverviewDashboard = ({ tasks, onTaskAction, onDismiss, onClearAllInformational }) => {
  const isActionable = (t) => t && t.isAction !== false && t.type !== 'RESOLUTION';

  const actionableTasks = tasks.filter(isActionable);
  const infoTasks = tasks.filter((t) => !isActionable(t));

  const priorityRank = (p) => (p === 'red' ? 0 : p === 'amber' ? 1 : 2);
  const createdMs = (t) => (t.createdAt?.toMillis ? t.createdAt.toMillis() : 0);

  const sortedActionableTasks = [...actionableTasks].sort((a, b) => {
    const pr = priorityRank(a.priority) - priorityRank(b.priority);
    if (pr !== 0) return pr;
    return createdMs(b) - createdMs(a);
  });

  const sortedInfoTasks = [...infoTasks].sort((a, b) => createdMs(b) - createdMs(a));

  const trafficLightColor = (task) => {
    if (!isActionable(task)) return '#94a3b8';
    if (task.priority === 'red') return '#f87171';
    if (task.priority === 'amber') return '#fbbf24';
    return '#94a3b8';
  };

  const TaskRow = ({ task }) => (
    <div 
      className={`task-row-item priority-${task.priority}`} 
      onClick={() => (isActionable(task) ? onTaskAction(task) : null)}
      style={{
        display: 'flex', alignItems: 'center', gap: '20px', padding: '15px 25px',
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '8px', cursor: isActionable(task) ? 'pointer' : 'default', transition: '0.2s', marginBottom: '8px'
      }}
    >
      <div
        aria-hidden="true"
        title={isActionable(task) ? (task.priority === 'red' ? 'High priority' : task.priority === 'amber' ? 'Standard priority' : 'Low priority') : 'Notification'}
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '999px',
          background: trafficLightColor(task),
          boxShadow: '0 0 0 2px rgba(15, 23, 42, 0.35) inset',
          flex: '0 0 auto'
        }}
      />
      
      <div className="task-content" style={{ flex: 1 }}>
        <div className="task-label" style={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{task.label}</div>
        <div className="task-meta" style={{ display: 'flex', gap: '15px', marginTop: '4px' }}>
          <span style={{ fontSize: '0.65rem', color: '#138a8a', fontWeight: '900', letterSpacing: '1px' }}>{(task.category || 'system').toUpperCase()}</span>
          {!isActionable(task) && <span style={{ fontSize: '0.65rem', opacity: 0.5, fontStyle: 'italic' }}>Notification</span>}
        </div>
      </div>

      <div className="task-actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button className="btn-row-action" style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '6px 12px', borderRadius: '4px', fontSize: '0.75rem' }}>
          {isActionable(task) ? 'Open' : 'FYI'}
        </button>
        
        {task.docId && (
          <button 
            className="task-dismiss-btn" 
            onClick={(e) => {
              e.stopPropagation(); // Prevents navigation from triggering when dismissing
              onDismiss(e, task);
            }}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '24px', height: '24px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}
            title="Dismiss"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="overview-dashboard">
      <header className="dashboard-header" style={{ marginBottom: '30px' }}>
        <h3 style={{ color: 'white', margin: 0, fontSize: '1.4rem' }}>Action Center</h3>
        <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '5px 0 0 0' }}>
          {actionableTasks.length} actions · {infoTasks.length} notifications
        </p>
      </header>

      {tasks.length === 0 ? (
        <div className="empty-state-card" style={{ textAlign: 'center', padding: '60px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '15px' }}>✅</div>
          <h4 style={{ color: 'white', margin: '0 0 10px 0' }}>Queue Clear</h4>
          <p style={{ color: '#94a3b8', margin: 0 }}>No pending tasks require your immediate attention. Great work!</p>
        </div>
      ) : (
        <div className="dashboard-grid">
          {sortedActionableTasks.length > 0 && (
            <section className="dashboard-section" style={{ marginBottom: '40px' }}>
              <h4 className="section-title" style={{ color: '#e2e8f0', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '15px', paddingLeft: '5px' }}>My Actions</h4>
              <div className="task-stack">
                {sortedActionableTasks.map((task, i) => <TaskRow key={`a-${i}`} task={task} />)}
              </div>
            </section>
          )}

          {sortedInfoTasks.length > 0 && (
            <section className="dashboard-section" style={{ marginTop: sortedActionableTasks.length ? '30px' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '15px', paddingLeft: '5px', paddingRight: '5px' }}>
                <h4 className="section-title" style={{ color: '#e2e8f0', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>My Notifications</h4>
                {typeof onClearAllInformational === 'function' && (
                  <button
                    type="button"
                    onClick={() => onClearAllInformational()}
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '8px 10px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="task-stack">
                {sortedInfoTasks.map((task, i) => <TaskRow key={`n-${i}`} task={task} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewDashboard;